import * as React from "react";
import { cn } from "../utils/cn";
import { Heading, Mono, Text } from "../primitives/typography";

const detailColorMap = {
    default: "text-muted-foreground",
    green: "text-success",
    red: "text-destructive",
    yellow: "text-warning",
} as const;

export type StatCardDetailColor = keyof typeof detailColorMap;

export interface StatCardProps extends React.HTMLAttributes<HTMLDivElement> {
    label: string;
    value: string | number;
    detail?: string;
    detailPrefix?: string;
    detailColor?: StatCardDetailColor;
    standalone?: boolean;
    /** A control beside the label — a checkbox choosing the card for an export, say. */
    action?: React.ReactNode;
}

const StatCard = React.forwardRef<HTMLDivElement, StatCardProps>(
    (
        { className, label, value, detail, detailPrefix, detailColor = "default", standalone = true, action, ...props },
        ref,
    ) => (
        <div
            ref={ref}
            className={cn(
                "flex flex-col gap-1",
                standalone && "border-border bg-card rounded-2xl border p-5",
                className,
            )}
            {...props}
        >
            {action ? (
                <div className="flex items-start justify-between gap-2">
                    <Mono size="xs" muted className="tracking-wider uppercase">
                        {label}
                    </Mono>
                    {action}
                </div>
            ) : (
                <Mono size="xs" muted className="tracking-wider uppercase">
                    {label}
                </Mono>
            )}
            <Heading level={3}>{value}</Heading>
            {detail && (
                <Text size="sm" as="p">
                    {detailPrefix && (
                        <Text as="span" size="xs" weight="medium" className={detailColorMap[detailColor]}>
                            {detailPrefix}{" "}
                        </Text>
                    )}
                    <Text
                        as="span"
                        size="xs"
                        className={detailPrefix ? "text-muted-foreground" : detailColorMap[detailColor]}
                    >
                        {detail}
                    </Text>
                </Text>
            )}
        </div>
    ),
);
StatCard.displayName = "StatCard";

export interface StatCardGroupProps extends React.HTMLAttributes<HTMLDivElement> {}

const StatCardGroup = React.forwardRef<HTMLDivElement, StatCardGroupProps>(({ className, children, ...props }, ref) => (
    <div
        ref={ref}
        className={cn(
            "border-border bg-card rounded-2xl border",
            "divide-border flex flex-col divide-y",
            "sm:flex-row sm:divide-x sm:divide-y-0",
            className,
        )}
        {...props}
    >
        {React.Children.map(children, (child) =>
            React.isValidElement(child) ? (
                <div className="min-w-0 flex-1 p-5">
                    {React.cloneElement(child as React.ReactElement<StatCardProps>, { standalone: false })}
                </div>
            ) : null,
        )}
    </div>
));
StatCardGroup.displayName = "StatCardGroup";

export { StatCard, StatCardGroup };
